import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";

export type UserRole =
  | "system_admin"
  | "org_owner"
  | "compliance_officer"
  | "admin"
  | "manager"
  | "broker"
  | "support"
  | "readonly";

interface RlsSessionContext {
  organisationId: string | null;
  userRole: UserRole | null;
  userId: string | null;
  rlsContextSet: boolean;
  serviceRoleBypassed: boolean;
  reqUser: {
    organisationId?: string;
    role?: string;
    userId?: string;
    email?: string;
  };
}

interface UseUserRoleResult {
  role: UserRole;
  organisationId: string | null;
  userId: string | null;
  isLoading: boolean;
  canSeeCompliance: boolean;
  canSeeCommission: boolean;
  canCreate: boolean;
}

const COMPLIANCE_ROLES: UserRole[] = ["system_admin", "org_owner", "compliance_officer", "admin"];
const COMMISSION_ROLES: UserRole[] = ["system_admin", "org_owner", "compliance_officer", "admin", "manager", "broker"];
const CREATE_ROLES: UserRole[] = ["system_admin", "org_owner", "compliance_officer", "admin", "manager", "broker", "support"];

export function useUserRole(): UseUserRoleResult {
  const { data, isLoading } = useQuery<RlsSessionContext>({
    queryKey: ["/api/auth/rls-session-context"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const role: UserRole = data?.userRole ?? "admin";
  const organisationId = data?.organisationId ?? null;
  const userId = data?.userId ?? null;

  return {
    role,
    organisationId,
    userId,
    isLoading,
    canSeeCompliance: COMPLIANCE_ROLES.includes(role),
    canSeeCommission: COMMISSION_ROLES.includes(role),
    canCreate: CREATE_ROLES.includes(role),
  };
}
